import { useEffect, useMemo, useState } from "react";
import Datatable from "../../../core/common/dataTable";
import { ColumnsType } from "antd/es/table";
import { getSearchableHeader } from "../../utils/getSearchableHeader";
import Footer from "../../../core/common/Footer";
import useApi from "../../../core/customhook/useApi";
import Swal from "sweetalert2";
import { useNavigate } from "react-router";
import { all_routes } from "../../router/all_routes";
import { cellWidth } from "../../../core/methods/tableCellWidth";

const ManageUsers = () => {
  const navigate = useNavigate();
  const [showGuide, setShowGuide] = useState(true);
  const [itemList, setItemList] = useState<any[]>([]);
  const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});

  // ---- server-side pagination ----
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [total, setTotal] = useState<number>(0);

  // page & limit query params me jaate hain
  const getUsers = useApi<any>(
    `/api/v1/admin/auth/users?page=${pagination.page}&limit=${pagination.limit}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );
  const updateUserStatus = useApi<any>("", {
    method: "PATCH",
    autoCall: false,
    handleGlobalError: false,
  });

  const loadUsers = async () => {
    try {
      const res = await getUsers.callApi();

      if (res?.success) {
        const data = res?.data;

        // response array ho ya { rows | table | users, pagination } object, dono chalega
        const rawUsers: any[] = Array.isArray(data)
          ? data
          : (data?.rows ?? data?.table ?? data?.users ?? []);

        const users = rawUsers.map((user: any) => {
          const profileMappings = user.profile_mappings || [];

          const projectNames = profileMappings
            .map((mapping: any) => mapping?.scope?.project_name)
            .filter(Boolean);
          const programNames = profileMappings
            .map((mapping: any) => mapping?.scope?.program_name)
            .filter(Boolean);

          const departmentNames = profileMappings.flatMap((mapping: any) =>
            (mapping?.scope?.departments || [])
              .map((department: any) => department?.department_name)
              .filter(Boolean),
          );

          return {
            key: user.user_id,
            user_id: user.user_id,
            name: user.name,
            email: user.email,
            mobile: user.mobile,
            is_active: user.is_active,
            status: user.status,
            created_at: user.created_at,
            last_login: user.last_login,

            project_name: projectNames,
            program_name: programNames,
            department_name: departmentNames,
          };
        });

        setItemList(users);

        const pg = data?.pagination;
        setTotal(
          pg?.total ??
            pg?.totalRecords ??
            (Array.isArray(data) ? data.length : users.length),
        );
      } else {
        setItemList([]);
        setTotal(0);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to load users.",
      });
    }
  };

  // page ya limit badalne par dobara fetch
  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit]);

  // ---- Filtered Data (sirf current page par) ----
  const filteredData = useMemo(() => {
    return itemList.filter((row) =>
      Object.entries(columnSearch).every(([key, value]) => {
        if (!value) return true;

        const field = row[key];

        if (Array.isArray(field)) {
          return field.join(" ").toLowerCase().includes(value.toLowerCase());
        }

        return field?.toString().toLowerCase().includes(value.toLowerCase());
      }),
    );
  }, [itemList, columnSearch]);

  const handleStatusToggle = async (record: any) => {
    const previousStatus = record.is_active;
    const newStatus = !previousStatus;
    setItemList((prev: any[]) =>
      prev.map((item) =>
        item.key === record.key ? { ...item, is_active: newStatus } : item,
      ),
    );

    try {
      const payload = { is_active: newStatus };

      const res = await updateUserStatus.callApi(
        payload,
        `/api/v1/admin/auth/users/${record.key}/status`,
      );

      if (!res?.success) {
        throw new Error(res?.message || "Unable to update user status.");
      }
    } catch (error: any) {
      setItemList((prev: any[]) =>
        prev.map((item) =>
          item.key === record.key
            ? { ...item, is_active: previousStatus }
            : item,
        ),
      );

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to update user status.",
      });
    }
  };

  const handleTableChange = (pgConfig: any) => {
    const newPage = pgConfig.current;
    const newLimit = pgConfig.pageSize;

    if (newPage === pagination.page && newLimit === pagination.limit) return;

    setPagination({
      // page size badalne par first page par jao
      page: newLimit !== pagination.limit ? 1 : newPage,
      limit: newLimit,
    });
  };

  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      width: 80,
      align: "center",
      render: (_: any, __: any, index: number) =>
        (pagination.page - 1) * pagination.limit + index + 1,
    },
    {
      title: getSearchableHeader("User Name", "name", setColumnSearch),
      dataIndex: "name",
      key: "name",
      onCell:()=> cellWidth(200),
      sorter: (a: any, b: any) => (a.name || "").localeCompare(b.name || ""),
      render: (text: string, record: any) => (
        <span
          role="link"
          tabIndex={0}
          className="text-primary"
          style={{
            cursor: "pointer",
            textDecoration: "underline",
          }}
          onClick={() =>
            navigate(all_routes.userDetails, {
              state: { user: record },
            })
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              navigate(all_routes.userDetails, {
                state: { user: record },
              });
            }
          }}
        >
          {text || "--"}
        </span>
      ),
    },

    {
      title: getSearchableHeader("Email Address", "email", setColumnSearch),
      dataIndex: "email",
      key: "email",
      onCell:()=> cellWidth(200),
      sorter: (a: any, b: any) => (a.email || "").localeCompare(b.email || ""),
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
    },
    {
      title: getSearchableHeader("Mobile Number", "mobile", setColumnSearch),
      dataIndex: "mobile",
      key: "mobile",
      onCell:()=> cellWidth(200),
      sorter: (a: any, b: any) =>
        (a.mobile || "").localeCompare(b.mobile || ""),
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      align: "center",
      onCell:()=> cellWidth(100),
      render: (_: any, record: any) => (
        <div className="form-check form-switch d-flex justify-content-center">
          <input
            className="form-check-input"
            type="checkbox"
            checked={Boolean(record.is_active)}
            onChange={() => handleStatusToggle(record)}
          />
        </div>
      ),
    },
  ];

  return (
    // <div className="page-wrapper">
    //   <div className="content">
    //     <div className="page-header">
    //       <div className="row align-items-center">
    //         <div className="col-sm-12">
    //           <h3 className="page-title">Manage Users</h3>

    //           <ul className="breadcrumb">
    //             <li className="breadcrumb-item">Account Activation</li>

    //             <li className="breadcrumb-item">User Management</li>

    //             <li className="breadcrumb-item active">Manage Users</li>
    //           </ul>
    //         </div>
    //       </div>
    //     </div>
    //     <div className="text-end mt-3">
    //       <button
    //         className="btn border bg-white px-3"
    //         onClick={() => setShowGuide((prev) => !prev)}
    //       >
    //         <i className="ti ti-info-circle-filled fs-16"></i> Guide
    //       </button>
    //     </div>
    //     {showGuide && (
    //       <div
    //         style={{
    //           overflow: "hidden",
    //           maxHeight: showGuide ? "500px" : "0px",
    //           transition: "all 0.4s ease",
    //         }}
    //       >
    //         <div className="text-end pe-3">
    //           <i
    //             className="ti ti-triangle-inverted-filled"
    //             style={{
    //               fontSize: "22px",
    //               color: "#cfcfcf",
    //               lineHeight: 0,
    //             }}
    //           ></i>
    //         </div>
    //         <div
    //           className="card border-0 shadow-sm"
    //           style={{ borderRadius: "4px" }}
    //         >
    //           <div className="card-body position-relative p-4">
    //             <div className="position-absolute top-0 end-0 mt-3 me-3">
    //               <i
    //                 className="ti ti-x"
    //                 style={{
    //                   background: "#6c757d",
    //                   color: "#fff",
    //                   borderRadius: "50%",
    //                   width: "20px",
    //                   height: "20px",
    //                   fontSize: "12px",
    //                   cursor: "pointer",
    //                   display: "inline-flex",
    //                   alignItems: "center",
    //                   justifyContent: "center",
    //                 }}
    //                 onClick={() => setShowGuide(false)}
    //               ></i>
    //             </div>

    //             <h5 className="fw-bold mb-3">Manage Users</h5>

    //             <div>
    //               <ul
    //                 className="ps-3 mb-3"
    //                 style={{
    //                   listStyleType: "disc",
    //                   color: "#212529",
    //                 }}
    //               >
    //                 <li>
    //                   {/* This tab is used to invite client users to the application
    //                   and assign the appropriate account access. */}
    //                   This page displays the list of users available in the
    //                   application.
    //                 </li>

    //                 <li>
    //                   {/* Relevant menus and action permissions can be enabled based
    //                   on user requirements. */}
    //                   Use the search fields to find users by their available
    //                   details.
    //                 </li>

    //                 <li>
    //                   {/* Predefined roles are available, and custom roles can also
    //                   be created. */}
    //                   Click on a username to view the user's Account, Program,
    //                   and Department details on a separate page.
    //                 </li>

    //                 <li>
    //                   {/* Click on <strong>Action</strong> to modify a user's access
    //                   level and permissions. */}
    //                   Use the <strong> Active/Inactive</strong> toggle to update
    //                   a user's account status.
    //                 </li>
    //               </ul>

    //               <p className="mb-0">
    //                 <strong>Pls Note:</strong> Ensure that user access and
    //                 account status are configured appropriately. Changes to a
    //                 user's status may affect their ability to access the
    //                 application.
    //               </p>
    //             </div>
    //           </div>
    //         </div>
    //       </div>
    //     )}
    //     <div className="card mt-3">
    //       <Datatable
    //         dataSource={filteredData}
    //         columns={columns}
    //         fixedHeader
    //         tableHeight={650}
    //         Selection={false}
    //         rowKey="user_id"
    //         pagination={{
    //           current: pagination.page,
    //           pageSize: pagination.limit,
    //           total,
    //         }}
    //         handleTableChange={handleTableChange}
    //       />
    //     </div>
    //   </div>

    //   <Footer />
    // </div>
    <div className="page-wrapper">
  <div className="content">
    {/* Header: title + breadcrumb on the left, Guide button on the right */}
    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
      <div className="my-auto mb-2">
        <h2 className="mb-1">Manage Users</h2>
        <nav>
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">Account Activation</li>
            <li className="breadcrumb-item">User Management</li>
            <li className="breadcrumb-item active">Manage Users</li>
          </ol>
        </nav>
      </div>

      <button
        type="button"
        className="btn d-flex align-items-center gap-2 px-3 mb-2"
        onClick={() => setShowGuide((prev) => !prev)}
        style={{
          background: showGuide ? "#E1F5EE" : "#fff",
          color: showGuide ? "#085041" : "#2C2C2A",
          border: showGuide ? "1px solid #0F6E56" : "1px solid #E5E3DC",
          transition: "all .2s ease",
        }}
      >
        <i className="ti ti-info-circle-filled fs-16"></i>
        <span>Guide</span>
      </button>
    </div>

    {/* Guide panel: stays mounted so it can animate open and closed */}
    <div
      style={{
        overflow: "hidden",
        maxHeight: showGuide ? "500px" : "0px",
        opacity: showGuide ? 1 : 0,
        marginBottom: showGuide ? "16px" : "0px",
        transition: "all 0.4s ease",
      }}
    >
      <div
        className="card border-0 shadow-sm mb-0"
        style={{
          borderRadius: "8px",
          borderLeft: "4px solid #0F6E56",
          backgroundColor: "#F8FCFA",
        }}
      >
        <div className="card-body position-relative p-4">
          <button
            type="button"
            aria-label="Close guide"
            className="position-absolute top-0 end-0 mt-3 me-3 border-0 d-inline-flex align-items-center justify-content-center"
            onClick={() => setShowGuide(false)}
            style={{
              background: "#6c757d",
              color: "#fff",
              borderRadius: "50%",
              width: "22px",
              height: "22px",
              fontSize: "12px",
              cursor: "pointer",
              padding: 0,
            }}
          >
            <i className="ti ti-x"></i>
          </button>

          <div className="d-flex align-items-center gap-2 mb-3">
            <i
              className="ti ti-users"
              style={{ fontSize: "20px", color: "#0F6E56" }}
            ></i>
            <h5 className="fw-bold mb-0">Manage Users</h5>
          </div>

          <ul className="list-unstyled mb-3">
            {[
              <>
                View and manage users registered in the application.
              </>,
              <>Use the search fields to find users by name, email address, or mobile number.</>,
              <>
                Click a user's name to view their assigned accounts, programs, and departments.
              </>,
              <>
                Use the status toggle to activate or deactivate a user account.
              </>,
            ].map((item, i) => (
              <li key={i} className="d-flex align-items-start gap-2 mb-2">
                <i
                  className="ti ti-circle-check-filled"
                  style={{ color: "#0F6E56", fontSize: "16px", marginTop: 3 }}
                ></i>
                <span style={{ color: "#212529" }}>{item}</span>
              </li>
            ))}
          </ul>

          <div
            className="d-flex align-items-start gap-2 rounded-3 px-3 py-2"
            style={{ backgroundColor: "#FFF8E6", border: "1px solid #F5E1A4" }}
          >
            <i
              className="ti ti-alert-triangle-filled"
              style={{ color: "#B7791F", fontSize: "16px", marginTop: 3 }}
            ></i>
            <span className="mb-0" style={{ color: "#5C4A12" }}>
              <strong>Important: </strong> Ensure user access and account status are configured correctly. Deactivating a user may prevent them from accessing the application.
            </span>
          </div>
        </div>
      </div>
    </div>

    {/* Users table */}
    <div className="card border-0 shadow-sm mt-3">
      <Datatable
        dataSource={filteredData}
        columns={columns}
        fixedHeader
        tableHeight={650}
        Selection={false}
        rowKey="user_id"
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total,
        }}
        handleTableChange={handleTableChange}
      />
    </div>
  </div>

  <Footer />
</div>
  );
};

export default ManageUsers;

// import { useEffect, useMemo, useState } from "react";
// import Datatable from "../../../core/common/dataTable";
// import { ColumnsType } from "antd/es/table";
// import { getSearchableHeader } from "../../utils/getSearchableHeader";
// import { cellWidth } from "../../../core/methods/tableCellWidth";
// import Footer from "../../../core/common/Footer";
// import useApi from "../../../core/customhook/useApi";
// import Swal from "sweetalert2";
// import DepartmentCell from "./DepartmentCell";

// const ManageUsers = () => {
//   const [showGuide, setShowGuide] = useState(true);
//   const [itemList, setItemList] = useState<any[]>([]);
//   const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});
//   const getUsers = useApi("/api/v1/admin/auth/users", {
//     method: "GET",
//     autoCall: false,
//     handleGlobalError: false,
//   });
//   const updateUserStatus = useApi("", {
//     method: "PATCH",
//     autoCall: false,
//     handleGlobalError: false,
//   });
//   useEffect(() => {
//     const loadUsers = async () => {
//       try {
//         const res = await getUsers.callApi();

//         if (res?.success) {
//           const users = (res.data || []).map((user: any, index: number) => {
//             const profileMappings = user.profile_mappings || [];

//             const projectNames = profileMappings
//               .map((mapping: any) => mapping?.scope?.project_name)
//               .filter(Boolean);
//             const programNames = profileMappings
//               .map((mapping: any) => mapping?.scope?.program_name)
//               .filter(Boolean);

//             const departmentNames = profileMappings.flatMap((mapping: any) =>
//               (mapping?.scope?.departments || [])
//                 .map((department: any) => department?.department_name)
//                 .filter(Boolean),
//             );

//             return {
//               key: user.user_id,
//               sno: index + 1,
//               user_id: user.user_id,
//               name: user.name,
//               email: user.email,
//               mobile: user.mobile,
//               is_active: user.is_active,
//               status: user.status,
//               created_at: user.created_at,
//               last_login: user.last_login,

//               project_name: projectNames,
//               program_name: programNames,
//               department_name: departmentNames,
//             };
//           });

//           setItemList(users);
//         }
//       } catch (error) {
//         Swal.fire({
//           icon: "error",
//           title: "Error",
//           text: "Unable to load users.",
//         });
//       }
//     };

//     loadUsers();
//   }, []);

//   const filteredData = useMemo(() => {
//     return itemList.filter((row) =>
//       Object.entries(columnSearch).every(([key, value]) => {
//         if (!value) return true;

//         const field = row[key];

//         if (Array.isArray(field)) {
//           return field.join(" ").toLowerCase().includes(value.toLowerCase());
//         }

//         return field?.toString().toLowerCase().includes(value.toLowerCase());
//       }),
//     );
//   }, [itemList, columnSearch]);

//   const handleStatusToggle = async (record: any) => {
//     const previousStatus = record.is_active;
//     const newStatus = !previousStatus;
//     setItemList((prev: any[]) =>
//       prev.map((item) =>
//         item.key === record.key
//           ? {
//               ...item,
//               is_active: newStatus,
//             }
//           : item,
//       ),
//     );

//     try {
//       const payload = {
//         is_active: newStatus,
//       };

//       const res = await updateUserStatus.callApi(
//         payload,
//         `/api/v1/admin/auth/users/${record.key}/status`,
//       );

//       if (!res?.success) {
//         throw new Error(res?.message || "Unable to update user status.");
//       }
//     } catch (error: any) {
//       setItemList((prev: any[]) =>
//         prev.map((item) =>
//           item.key === record.key
//             ? {
//                 ...item,
//                 is_active: previousStatus,
//               }
//             : item,
//         ),
//       );

//       Swal.fire({
//         icon: "error",
//         title: "Update Failed",
//         text:
//           error?.response?.data?.message ||
//           error?.message ||
//           "Unable to update user status.",
//       });
//     }
//   };

//   const columns: ColumnsType<any> = [
//     {
//       title: "Sr. No.",
//       key: "serial_number",
//       dataIndex: "serial_number",
//       width: 80,
//       align: "center",
//     },
//     {
//       title: getSearchableHeader("Account", "project_name", setColumnSearch),
//       dataIndex: "project_name",
//       key: "project_name",
//       onCell: () => cellWidth(160),

//       render: (projects: string[]) =>
//         projects?.length ? (
//           <div className="d-flex flex-wrap gap-1">
//             {projects.map((item: string, index: number) => (
//               <span
//                 key={`${item}-${index}`}
//                 className="badge rounded-pill"
//                 style={{
//                   background: "#EEF4FF",
//                   color: "#0D6EFD",
//                   border: "1px solid #CFE2FF",
//                   padding: "6px 10px",
//                   fontWeight: 500,
//                   marginBottom: 4,
//                 }}
//               >
//                 {item}
//               </span>
//             ))}
//           </div>
//         ) : (
//           <span className="text-muted">--</span>
//         ),
//     },
//     {
//       title: getSearchableHeader("Program", "program_name", setColumnSearch),
//       dataIndex: "program_name",
//       key: "program_name",
//       onCell: () => cellWidth(160),

//       render: (programs: string[]) =>
//         programs?.length ? (
//           <div className="d-flex flex-wrap gap-1">
//             {programs.map((item: string, index: number) => (
//               <span
//                 key={`${item}-${index}`}
//                 className="badge rounded-pill"
//                 style={{
//                   background: "#EDFDF3",
//                   color: "#198754",
//                   border: "1px solid #BFE7CF",
//                   padding: "6px 10px",
//                   fontWeight: 500,
//                   marginBottom: 4,
//                 }}
//               >
//                 {item}
//               </span>
//             ))}
//           </div>
//         ) : (
//           <span className="text-muted">--</span>
//         ),
//     },
//     // {
//     //   title: getSearchableHeader(
//     //     "Department",
//     //     "department_name",
//     //     setColumnSearch,
//     //   ),
//     //   dataIndex: "department_name",
//     //   key: "department_name",
//     //   onCell: () => cellWidth(700),

//     //   render: (departments: string[]) =>
//     //     departments?.length ? (
//     //       <div className="d-flex flex-wrap gap-1">
//     //         {departments.map((item: string, index: number) => (
//     //           <span
//     //             key={`${item}-${index}`}
//     //             className="badge rounded-pill"
//     //             style={{
//     //               background: "#FFF8E8",
//     //               color: "#B45309",
//     //               border: "1px solid #FFE2A8",
//     //               padding: "6px 10px",
//     //               fontWeight: 500,
//     //               marginBottom: 4,
//     //             }}
//     //           >
//     //             {item}
//     //           </span>
//     //         ))}
//     //       </div>
//     //     ) : (
//     //       <span className="text-muted">--</span>
//     //     ),
//     // },
//     {
//       title: getSearchableHeader(
//         "Department",
//         "department_name",
//         setColumnSearch,
//       ),
//       dataIndex: "department_name",
//       key: "department_name",
//       onCell: () => cellWidth(300), // was 700, no longer needed
//       render: (departments: string[], record: any) => (
//         <DepartmentCell
//           departments={departments}
//           userName={record.name}
//           previewCount={2}
//         />
//       ),
//     },
//     {
//       title: getSearchableHeader("Username", "name", setColumnSearch),
//       dataIndex: "name",
//       key: "name",
//       sorter: (a: any, b: any) => (a.name || "").localeCompare(b.name || ""),
//     },
//     {
//       title: getSearchableHeader("Email", "email", setColumnSearch),
//       dataIndex: "email",
//       key: "email",
//       sorter: (a: any, b: any) => (a.email || "").localeCompare(b.email || ""),
//       onHeaderCell: () => ({
//         style: {
//           whiteSpace: "nowrap",
//         },
//       }),
//     },
//     {
//       title: getSearchableHeader("Phone", "mobile", setColumnSearch),
//       dataIndex: "mobile",
//       key: "mobile",
//       sorter: (a: any, b: any) =>
//         (a.mobile || "").localeCompare(b.mobile || ""),
//       onHeaderCell: () => ({
//         style: {
//           whiteSpace: "nowrap",
//         },
//       }),
//     },
//     {
//       title: "Active/Inactive",
//       dataIndex: "is_active",
//       key: "is_active",
//       align: "center",

//       render: (_: any, record: any) => (
//         <div className="form-check form-switch d-flex justify-content-center">
//           <input
//             className="form-check-input"
//             type="checkbox"
//             checked={Boolean(record.is_active)}
//             onChange={() => handleStatusToggle(record)}
//           />
//         </div>
//       ),
//     },
//   ];

//   return (
//     <div className="page-wrapper">
//       <div className="content">
//         <div className="page-header">
//           <div className="row align-items-center">
//             <div className="col-sm-12">
//               <h3 className="page-title">Manage Users</h3>

//               <ul className="breadcrumb">
//                 <li className="breadcrumb-item">Account Activation</li>

//                 <li className="breadcrumb-item">User Management</li>

//                 <li className="breadcrumb-item active">Manage Users</li>
//               </ul>
//             </div>
//           </div>
//         </div>
//         <div className="text-end mt-3">
//           <button
//             className="btn border bg-white px-3"
//             onClick={() => setShowGuide((prev) => !prev)}
//           >
//             <i className="ti ti-info-circle-filled fs-16"></i> Guide
//           </button>
//         </div>
//         {showGuide && (
//           <div
//             style={{
//               overflow: "hidden",
//               maxHeight: showGuide ? "500px" : "0px",
//               transition: "all 0.4s ease",
//             }}
//           >
//             <div className="text-end pe-3">
//               <i
//                 className="ti ti-triangle-inverted-filled"
//                 style={{
//                   fontSize: "22px",
//                   color: "#cfcfcf",
//                   lineHeight: 0,
//                 }}
//               ></i>
//             </div>
//             <div
//               className="card border-0 shadow-sm"
//               style={{
//                 borderRadius: "4px",
//               }}
//             >
//               <div className="card-body position-relative p-4">
//                 <div className="position-absolute top-0 end-0 mt-3 me-3">
//                   <i
//                     className="ti ti-x"
//                     style={{
//                       background: "#6c757d",
//                       color: "#fff",
//                       borderRadius: "50%",
//                       width: "20px",
//                       height: "20px",
//                       fontSize: "12px",
//                       cursor: "pointer",
//                       display: "inline-flex",
//                       alignItems: "center",
//                       justifyContent: "center",
//                     }}
//                     onClick={() => setShowGuide(false)}
//                   ></i>
//                 </div>

//                 <h5 className="fw-bold mb-3">Manage Users</h5>

//                 <div>
//                   <ul
//                     className="ps-3 mb-3"
//                     style={{
//                       listStyleType: "disc",
//                       color: "#212529",
//                     }}
//                   >
//                     <li>
//                       This tab is used to invite client users to the application
//                       and assign the appropriate account access.
//                     </li>

//                     <li>
//                       Relevant menus and action permissions can be enabled based
//                       on user requirements.
//                     </li>

//                     <li>
//                       Predefined roles are available, and custom roles can also
//                       be created.
//                     </li>

//                     <li>
//                       Click on <strong>Action</strong> to modify a user's access
//                       level and permissions.
//                     </li>
//                   </ul>

//                   <p className="mb-0">
//                     <strong>Pls Note:</strong> User access and permissions
//                     should ideally be configured in consultation with the client
//                     during a call. Unauthorized changes may grant access to
//                     sensitive reports or data and could result in client
//                     escalations.
//                   </p>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}
//         <div className="card mt-3">
//           <Datatable
//             dataSource={filteredData}
//             columns={columns}
//             Selection={false}
//           />
//         </div>
//       </div>

//       <Footer />
//     </div>
//   );
// };

// export default ManageUsers;
